import { custInfo } from "./customer";

/** Convert Celsius to Fahrenheit. */
export const cToF = (c: number): number => (c * 9) / 5 + 32;

/** Convert Fahrenheit to Celsius. */
export const fToC = (f: number): number => ((f - 32) * 5) / 9;

/** Does the logged-in customer prefer Fahrenheit? */
export const prefersFahrenheit = (): boolean =>
 custInfo?.customerPreferences?.temperatureUnit === "°F";

/** Unit symbol for the customer's preferred temperature unit ("°C" or "°F"). */
export const temperatureUnit = (): string =>
 prefersFahrenheit() ? "°F" : "°C";

/**
 * Format a temperature value according to the customer's preference.
 *
 * The stored value is always in °C. This function converts to °F if the
 * customer prefers Fahrenheit, rounds to one decimal, and optionally
 * appends the appropriate unit symbol.
 *
 * @param celsius - temperature in °C
 * @param showUnits - whether to append the unit symbol (default `true`)
 * @returns formatted string e.g. `"68.0 °F"` or `"20.0 °C"` (with units),
 *          or `"68.0"` / `"20.0"` when `showUnits` is `false`
 */
export const formatTemperature = (
 celsius: number,
 showUnits = true,
): string => {
 const useF = prefersFahrenheit();
 const value = useF ? cToF(celsius) : celsius;
 if (!showUnits) return `${value.toFixed(1)}`;
 const unit = temperatureUnit();
 return `${value.toFixed(1)}${unit}`;
};

/** Round half away from zero, matching the C library's `roundf`. */
const roundHalfAwayFromZero = (x: number): number =>
 Math.sign(x) * Math.round(Math.abs(x));

/**
 * Convert a Celsius *setpoint* to Fahrenheit, applying the same "snap"
 * algorithm used by the controller firmware.
 *
 * Since the temperature is always stored in Celsius, sometimes the rounding
 * causes it to be impossible to get to an integer setpoint in Fahrenheit.
 * This "snaps" it to the integer if it is within 0.09, so that's more
 * visually pleasing.
 *
 * Ported from the controller firmware:
 *
 *     float snap_to_fahrenheit(float celcius)
 *     {
 *         float result = celcius * 1.8 + 32;
 *         if (abs(roundf(result) - result) < 0.09)
 *         {
 *             return roundf(result);
 *         }
 *         return result;
 *     }
 */
export const snapToF = (celsius: number): number => {
 const result = celsius * 1.8 + 32;
 if (Math.abs(roundHalfAwayFromZero(result) - result) < 0.09) {
  return roundHalfAwayFromZero(result);
 }
 return result;
};

/**
 * Format a temperature *setpoint* according to the customer's preference.
 *
 * Setpoints are always stored internally in °C (in tenths). When the
 * customer prefers Fahrenheit, the controller firmware's snap-to-integer
 * algorithm (see `snapToF`) is applied so that integer setpoints look
 * visually pleasing: e.g. a stored setpoint of 18.3 °C (64.94 °F) is
 * displayed as `"65 °F"`.
 *
 * @param celsius - setpoint in °C
 * @param showUnits - whether to append the unit symbol (default `true`)
 * @returns e.g. `"65 °F"`, `"64.9 °F"` or `"20.0 °C"` (with units),
 *          or `"65"` / `"20.0"` when `showUnits` is `false`
 */
export const formatSetpoint = (celsius: number, showUnits = true): string => {
 if (!prefersFahrenheit()) return formatTemperature(celsius, showUnits);
 const value = snapToF(celsius);
 const text = Number.isInteger(value) ? value.toFixed(0) : value.toFixed(1);
 return showUnits ? `${text}°F` : text;
};
